import useOptimisticUpdateCustomer from '@/api/mutations/customers/optimistic/useOptimisticUpdateCustomer';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import UpdateCustomerModal from '@/ui/components/updateCustomerModal/UpdateCustomerModal';
import CustomerPageHeaders from '@/ui/pages/customerPage/components/CustomerPageHeaders';
import CustomerProjectTable from '@/ui/pages/customerPage/components/CustomerProjectTable';
import CustomerServicePlansTab from '@/ui/pages/customerPage/components/CustomerServicePlansTab';
import AuthenticatedPageWrapper from '@/ui/pages/pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { Card } from '@payaca/ambrosia';
import RouterTabs, {
  type TabPanel,
} from '@payaca/components/routerTabs/RouterTabs';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import * as customerActions from '@payaca/store/customer/customerActions';
import { clsx } from '@payaca/utilities/cvaMerge';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import useGetCustomer from '../../../api/queries/customers/useGetCustomer';
import { useSelector } from '../../../api/state';
import { getUserRoles } from '../../../utils/stateAccessors';
import { CustomFieldGroup } from '../../components/customFieldGroup/CustomFieldGroup';
import CustomerAddressTable from './components/CustomerAddressTable';
import CustomerContactsTable from './components/CustomerContactsTable';
import CustomerScheduleTab from './components/CustomerScheduleTab';
import FileSharingSettings from './components/FileSharingSettings';

export const CustomerPageRedirect = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { customer } = useGetCustomer(customerId);
  if (customer?.publicId) {
    return <Redirect to={`/customers/${customer.publicId}`} />;
  }
};
const CustomerPage: FC = () => {
  const userRoles = useSelector(getUserRoles);

  /**
   * Routing
   */
  const { customerId } = useParams<{ customerId: string }>();

  /**
   * State
   */
  const [isEditCustomerNameModalOpen, setIsEditCustomerNameModalOpen] =
    useState(false);

  /**
   * Queries
   */
  const { customer } = useGetCustomer(customerId);

  /**
   * Mutations
   */
  const { mutate: updateCustomer } = useOptimisticUpdateCustomer(customerId);

  /**
   * Feature flags
   */
  const hasServicePlansFeature = useMyAccountHasFeature('servicePlans');

  /**
   * TEMPORARY
   */
  const dispatch = useDispatch();
  useEffect(() => {
    if (customer) {
      dispatch(customerActions.requestGetCustomer(+customer.id));
    }
  }, [customer, dispatch]);
  /**
   * END: TEMPORARY
   */

  const handleEditCustomerName = (name: string) => {
    updateCustomer({ customerId, name });
  };

  const routerTabPanels = useMemo<TabPanel[]>(() => {
    return [
      {
        icon: 'lightbulb-05.3',
        label: 'Projects',
        slug: 'projects',
        render: () => (
          <PermissionGuard
            renderIfHasPermissions={[DealsPermissions.GET_LISTED_DEALS]}
            placeholderMessage="You don't have permission to view customer Projects"
          >
            <CustomerProjectTable />
          </PermissionGuard>
        ),
      },
      {
        icon: 'calendar-check-02.3',
        label: 'Schedule',
        slug: 'schedule',
        render: () => <CustomerScheduleTab />,
      },
      {
        icon: 'home-02.3',
        label: 'Addresses',
        slug: 'addresses',
        render: () => <CustomerAddressTable />,
      },
      {
        icon: 'users-01.3',
        label: 'Contacts',
        slug: 'contacts',
        render: () => <CustomerContactsTable />,
      },
      ...(hasServicePlansFeature
        ? [
            {
              icon: 'repeat-04.3' as const,
              label: 'Service Plans',
              slug: 'servicePlans',
              render: () => <CustomerServicePlansTab />,
            },
          ]
        : []),
      {
        icon: 'settings-01.3',
        label: 'Settings',
        slug: 'settings',
        render: () => {
          return (
            <Card sizeVariant="sm">
              <Card.Body>
                <FileSharingSettings />
              </Card.Body>
            </Card>
          );
        },
      },
    ];
  }, [hasServicePlansFeature]);

  const canManageCustomFieldValues = userHasRequiredPermission(userRoles, [
    AccountsPermissions.MANAGE_CUSTOM_FIELD_VALUES,
  ]);

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Customers', to: '/customers' },
        { label: customer?.name || '' },
      ]}
    >
      <div className="md:grid-template-desktop flex flex-col md:grid md:items-start">
        <RouterTabs panels={routerTabPanels}>
          <div
            className={clsx(
              'grid-area-item1 p-6 pb-0 md:pb-4 md:pe-4',
              !canManageCustomFieldValues && 'col-span-full'
            )}
          >
            <Card>
              <Card.Body>
                <CustomerPageHeaders
                  onEditClick={() => setIsEditCustomerNameModalOpen(true)}
                />

                <RouterTabs.Nav
                  className="z-sticky sticky top-0 -mb-4 hidden items-end md:-mb-5 md:grid"
                  defaultTabSlug="projects"
                />
              </Card.Body>
            </Card>
          </div>

          <PermissionGuard
            renderIfHasPermissions={[
              AccountsPermissions.MANAGE_CUSTOM_FIELD_VALUES,
            ]}
          >
            <div className="grid-area-item2 p-6 md:row-span-2 md:h-full md:px-0 md:pe-4">
              <div className="h-full space-y-3.5 md:overflow-auto">
                {customer?.id && (
                  <CustomFieldGroup type="customer" customerId={customerId} />
                )}
              </div>
            </div>
          </PermissionGuard>

          <div className="grid-area-item3 p-6 pt-0 md:pe-4">
            <RouterTabs.Nav
              className={clsx(
                'z-sticky sticky top-[50px] mb-4 md:hidden',
                !canManageCustomFieldValues && 'col-span-full'
              )}
              defaultTabSlug="projects"
            />

            <RouterTabs.Panels />
          </div>
        </RouterTabs>
      </div>

      <UpdateCustomerModal
        isOpen={isEditCustomerNameModalOpen}
        title="Edit Customer Name"
        onClose={() => setIsEditCustomerNameModalOpen(false)}
        customerName={customer?.name || ''}
        onSubmit={({ name }) => {
          handleEditCustomerName(name);
          setIsEditCustomerNameModalOpen(false);
        }}
      />
    </AuthenticatedPageWrapper>
  );
};

export default CustomerPage;
